import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Checkbox, FormControlLabel, Stack, styled, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { MESSAGE_TYPES } from 'src/@types/message';
import { ReactComponent as CheckedIcon } from 'src/assets/icons/checked-icon.svg';
import { ReactComponent as UncheckedIcon } from 'src/assets/icons/unchecked-icon.svg';
import { SERVICE_CONTROL_PAGE_PATH, TERMS_TYPE_CODE } from 'src/assets/static/constants';
import CConfirmModal from 'src/components/custom/modal/CConfirmModal';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { Page } from 'src/components/Page';
import { useClickTracking } from 'src/hooks/useClickTracking';
import useWebViewCommunication from 'src/hooks/useWebViewCommunication';
import { LayoutProps } from 'src/layouts';
import { useStores } from 'src/models';
import { PATH_AUTH, PATH_PAGE } from 'src/routes/paths';
import { termsApi } from 'src/services';
import * as Yup from 'yup';

/**
 * ## 로그인
 *
 */

type FormValuesProps = {
  loginId: string;
  pwd: string;
};

export interface ILoginData {
  loginId: string;
  pwd: string;
  isDisconnectAll: boolean;
  isKeepLogin: boolean;
}

export const Login = observer(() => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { authStore } = useStores();
  const { login, getUserProfile } = authStore;
  const { sendMessage, isNativeApp } = useWebViewCommunication((message) => {
    if ('type' in message && message.type === MESSAGE_TYPES.ALERT) {
      handleAlert(message.payload.isOpen);
    }
  });
  const { withTracking } = useClickTracking();
  const { t } = useTranslation();

  const [keepLogin, setKeepLogin] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const PageTitle = t('auth.login.title');

  const { setHeaderProps } = useOutletContext<LayoutProps>();
  useEffect(() => {
    // set header props
    if (setHeaderProps) {
      setHeaderProps({
        leftTypes: ['back'],
        rightTypes: ['home'],
      });
    }
  }, [setHeaderProps]);

  const LoginSchema = Yup.object().shape({
    loginId: Yup.string().required(t('auth.login.enterId')).trim(),
    pwd: Yup.string().required(t('auth.login.enterPwd')),
  });

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues: { loginId: state?.findId ? state?.findId : '', pwd: '' },
  });

  const { getValues, handleSubmit } = methods;

  const callApiLogin = async (isDisconnectAll?: boolean) => {
    const loginData = {
      loginId: getValues('loginId'),
      pwd: getValues('pwd'),
      isDisconnectAll: isDisconnectAll ? isDisconnectAll : false,
      isKeepLogin: keepLogin,
    } as ILoginData;

    const result = await login(loginData);
    if (result?.resultCode === 'S') {
      const userProfileResult = await getUserProfile();
      if (userProfileResult?.resultCode === 'S') {
        // authguard wrapper로 인해 로그인 후 호출된 url로 이동하기 때문에 navigate 불필요
        // navigate(PATH_PAGE.home);
        // 디바이스 토큰 조회
        if (isNativeApp) {
          sendMessage({
            type: MESSAGE_TYPES.DEVICE_TOKEN,
          });
        }
      }
    } else {
      if (result?.errorCode === '409' && result.errorMessage) {
        if (isNativeApp) {
          sendMessage({
            type: MESSAGE_TYPES.ALERT,
            payload: {
              isOpen: true,
            },
          });
        }
        setErrorMessage(result.errorMessage);
      }
    }
  };

  const onSubmit = async () => {
    callApiLogin();
  };

  const handleAlert = (isOpen: boolean) => {
    if (!isOpen) {
      setErrorMessage(null);
    }
  };

  return (
    <Page title={PageTitle}>
      <Stack sx={{ gap: '40px' }}>
        <Title>
          <Trans i18nKey="auth.login.greeting" />
        </Title>

        <FormProvider
          methods={methods}
          onSubmit={handleSubmit(() => withTracking(SERVICE_CONTROL_PAGE_PATH.login, onSubmit)())}
        >
          <Stack sx={{ gap: '24px', mb: '40px' }}>
            <Stack sx={{ gap: '20px' }}>
              <RHFTextField variant="standard" name="loginId" placeholder={t('common.loginId')} />
              <RHFTextField
                variant="standard"
                name="pwd"
                type="password"
                placeholder={t('common.pwd')}
              />
            </Stack>
            <KeepLoginButton
              control={
                <Checkbox
                  checked={keepLogin}
                  icon={<UncheckedIcon />}
                  checkedIcon={<CheckedIcon />}
                  onChange={() => {
                    setKeepLogin(!keepLogin);
                  }}
                />
              }
              label={t('auth.login.keepLogin')}
            />
          </Stack>

          <Stack sx={{ gap: '10px' }}>
            <LoginButton type="submit" variant="contained">
              {t('auth.login.login')}
            </LoginButton>
          </Stack>
        </FormProvider>

        <LoginHelpContainer>
          <LoginHelpButton
            variant="text"
            onClick={withTracking(SERVICE_CONTROL_PAGE_PATH.findId, () =>
              navigate(PATH_AUTH.findId.root),
            )}
          >
            {t('auth.findId.title')}
          </LoginHelpButton>
          <LoginHelpIcon icon={'fluent:divider-short-16-regular'} />
          <LoginHelpButton
            variant="text"
            onClick={withTracking(SERVICE_CONTROL_PAGE_PATH.resetPwd, () =>
              navigate(PATH_AUTH.resetPwd.root),
            )}
          >
            {t('auth.resetPwd.title')}
          </LoginHelpButton>
          <LoginHelpIcon icon={'fluent:divider-short-16-regular'} />
          <LoginHelpButton
            variant="text"
            onClick={withTracking(SERVICE_CONTROL_PAGE_PATH.register, () =>
              navigate(PATH_AUTH.register.root),
            )}
          >
            {t('auth.register.title')}
          </LoginHelpButton>
        </LoginHelpContainer>

        <LoginHelpContainer>
          <PolicyButton
            variant="text"
            onClick={() => {
              const getTerms = async (termsTypeCd: number) => {
                const result = await termsApi.getTermsList(termsTypeCd);
                if (result?.resultCode === 'S') {
                  if (result.data && result.data?.length > 0) {
                    navigate(PATH_PAGE.policy, { state: { terms: result.data[0] } });
                  }
                }
              };
              getTerms(TERMS_TYPE_CODE.PRIVACY_POLICY);
            }}
          >
            {t('myPage.privacyPolicy.title')}
          </PolicyButton>
        </LoginHelpContainer>
      </Stack>

      {errorMessage && (
        <CConfirmModal
          isOpen={errorMessage !== null}
          title={errorMessage}
          confirmText={t('common.yes')}
          onConfirm={() => callApiLogin(true)}
          cancelText={t('common.no')}
          onCancel={() => setErrorMessage(null)}
        />
      )}
    </Page>
  );
});

export default Login;

const Title = styled(Typography)(() => ({ fontWeight: 700, fontSize: '28px', lineHeight: '42px' }));

const KeepLoginButton = styled(FormControlLabel)(() => ({
  '& .MuiFormControlLabel-label': {
    lineHeight: '21px',
  },
}));

const LoginButton = styled(Button)(() => ({
  height: '60px',
  borderRadius: '8px',
  fontSize: '16px',
  lineHeight: '24px',
}));

const LoginHelpContainer = styled(Stack)(() => ({
  justifyContent: 'space-around',
  flexDirection: 'row',
  alignItems: 'center',
}));

const LoginHelpIcon = styled(Iconify)(({ theme }) => ({
  color: theme.palette.grey[300],
}));

const LoginHelpButton = styled(Button)(({ theme }) => ({
  padding: 0,
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: theme.palette.common.black,
}));

const PolicyButton = styled(Button)(({ theme }) => ({
  padding: 0,
  fontWeight: 'bold',
  fontSize: '16px',
  lineHeight: '24px',
  color: '#2BB1E6',
}));
